import React, { useEffect, useState } from "react";

import { useForm, useWatch } from "react-hook-form";

import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import { apiFetch } from "../../../../apiBindings";
import Collapsible from "../../Collapsible";
import Checkbox from "../../forms/NewCheckbox";
import NewSelect from "../../forms/NewSelect";
import ReactHookInput from "../../forms/ReactHookInput";
import { ErrorWrapper } from "../../forms/ReactHookInput/ErroMessage";
import Image_ from "../../Image";
import Markdown from "../../Markdown";
import Button from "../../NewForms/Button";
import Headline from "../../NewHeadline";
import useSnackbar from "../../Snackbar/store";
import useStore, { iForm, Time } from "../../Workshop/store";
import Container from "../../Container";

const StyledContainer = styled.div`
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}
`;

const Form = styled.form`
    margin: 40px 0;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: repeat(2, 1fr);
       column-gap: 20px;
    `}
    ${({ theme }) => theme.breakpoints.from.medium.css`
       grid-template-columns: repeat(2, 1fr);

    `}
`;

const RowTwo = styled.div`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: repeat(2, 1fr);
       gap: 20px;
    `}
`;

const CheckboxRow = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`;

const ButtonWrapper = styled.div`
    margin-top: 40px;
    display: flex;
    justify-content: center;
`;

const Text = styled(Markdown)`
    margin-bottom: 20px;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

interface iHelpCollapsible {
    image: any;
    headline: string;
    text?: string;
}

interface iProps {
    headline: string;
    contactFormId: string;
    text?: string;
    helpCollapsible: iHelpCollapsible[];
}

const WorkshopContactForm = ({
    headline,
    contactFormId,
    text,
    helpCollapsible,
}: iProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [isPrivacyPolicyAcceptedError, setIsPrivacyPolicyAcceptedError] =
        useState<boolean>(false);
    const [activeProduct] = useStore((state) => [state.activeProduct], shallow);
    const { notify } = useSnackbar();

    const { handleSubmit, control, setValue, reset, watch } = useForm<iForm>({
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            time: "7to9",
            zipcode: "",
            carBrand: "",
            type: "",
            hsn: "",
            tsn: "",
            firstRegistration: "",
            message: "",
            isPrivacyPolicyAccepted: false,
        },
        mode: "onSubmit",
    });

    const time = useWatch({
        control,
        name: `time`,
    });

    const isPrivacyPolicyAccepted = useWatch({
        control,
        name: `isPrivacyPolicyAccepted`,
    });

    useEffect(() => {
        if (activeProduct) {
            setValue("message", `Komplettangebot: ${activeProduct.headline}`);
        }
    }, [activeProduct]);

    useEffect(() => {
        if (isPrivacyPolicyAccepted) {
            setIsPrivacyPolicyAcceptedError(false);
        }
    }, [isPrivacyPolicyAccepted]);

    const submit = async (data: iForm) => {
        if (!isPrivacyPolicyAccepted) {
            setIsPrivacyPolicyAcceptedError(true);
        } else {
            setLoading(true);
            apiFetch("/contact/workshop", {
                method: "post",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: JSON.stringify({ ...data, time: Time[data.time] }),
            })
                .then((response) => {
                    notify(
                        "Erfolgreich",
                        "Die Anfrage wurde erfolgreich abgeschickt."
                    );
                    reset();
                })
                .catch((error) =>
                    notify(
                        "Fehler",
                        "Leider ist ein Fehler aufgetreten bitte versuche es später erneut.",
                        "error"
                    )
                )
                .finally(() => setLoading(false));
        }
    };

    return (
        <StyledContainer id={contactFormId}>
            {headline && <Headline is="2">{headline}</Headline>}
            {text && <Text>{text}</Text>}
            <Container slim>
                {helpCollapsible.map((item) => {
                    return (
                        <Collapsible
                            title={item.headline}
                            defaultIsExpanded={false}
                            key={item.headline}
                        >
                            {item.image?.src && <Image_ {...item.image} />}
                            {item.text && <Markdown>{item.text}</Markdown>}
                        </Collapsible>
                    );
                })}
                <Form>
                    <Row>
                        <ReactHookInput
                            placeholder="Name"
                            name="name"
                            rules={{
                                required: true,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="E-Mail"
                            name="email"
                            rules={{
                                required: true,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="Telefon"
                            name="phone"
                            rules={{
                                required: true,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <NewSelect
                            name="time"
                            placeholder="Beste Anrufzeit"
                            value={time}
                            options={Object.keys(Time).map((key) => {
                                return {
                                    text: Time[key],
                                    value: key,
                                };
                            })}
                            onChange={(event: { target: { value: any } }) => {
                                setValue("time", event.target.value);
                            }}
                        />
                    </Row>
                    <Row>
                        <ReactHookInput
                            placeholder="Postleitzahl"
                            name="zipcode"
                            rules={{
                                required: true,
                                maxLength: 6,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="Automarke (optional)"
                            name="carBrand"
                            rules={{
                                required: false,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="Typ | Modell (optional)"
                            name="type"
                            rules={{
                                required: false,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="Erstzulassung (optional)"
                            name="firstRegistration"
                            rules={{
                                required: false,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                    </Row>
                    <RowTwo>
                        <ReactHookInput
                            placeholder="Herstellernummer (HSN) (optional)"
                            name="hsn"
                            rules={{
                                required: false,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                        <ReactHookInput
                            placeholder="Typschlüsselnummer (TSN) (optional)"
                            name="tsn"
                            rules={{
                                required: false,
                                maxLength: 40,
                            }}
                            control={control}
                            fullWidth
                        />
                    </RowTwo>
                    <ReactHookInput
                        placeholder="nähere Beschreibung"
                        name="message"
                        rules={{
                            required: true,
                            maxLength: 400,
                        }}
                        control={control}
                        fullWidth
                    />
                    <CheckboxRow>
                        <Checkbox
                            checked={isPrivacyPolicyAccepted}
                            onClick={() => {
                                setValue(
                                    "isPrivacyPolicyAccepted",
                                    !isPrivacyPolicyAccepted
                                );
                            }}
                            withField
                        >
                            <div>
                                Ich akzeptiere die{" "}
                                <a href="/datenschutz" target="_blank">
                                    Datenschutzerklärung
                                </a>
                            </div>
                        </Checkbox>
                        {isPrivacyPolicyAcceptedError && (
                            <ErrorWrapper>
                                <p>
                                    Du musst die Datenschutzerklärung
                                    akzeptieren, um fortfahren zu können.
                                </p>
                            </ErrorWrapper>
                        )}
                    </CheckboxRow>

                    <ButtonWrapper>
                        <Button
                            onClick={handleSubmit(submit)}
                            loading={loading}
                        >
                            Anfrage senden
                        </Button>
                    </ButtonWrapper>
                </Form>
            </Container>
        </StyledContainer>
    );
};

export default WorkshopContactForm;
